import { useCallback, useEffect, useRef } from 'react';
import { useAppFetcher } from '~/hooks/use-app-fetcher';
import { useLatest } from 'ahooks';
import { useAtomValue } from 'jotai';
import { useTracker } from '@archipro-website/tracker';
import { uiContextAtomDisplayMode } from '@rocco/states/uiContextAtomDisplayMode';
import type { action } from '~/routes/remix-api.home.toggle-featured-brand-pin';
import { TOGGLE_FEATURED_BRAND_PIN } from '~/routes/remix-api.home.toggle-featured-brand-pin';
import { fetcherIsDone } from '~/utils/fetcherHelper';
import { useRootPermissions, useUserLoggedIn } from '~/modules/user';

type CallbackFn = (error: { __typename: string; Message: string }) => void;
type SuccessCallbackFn = (pinned: boolean) => void;

/**
 * Hook to generate the function to toggle the featured brand pin status of a
 * professional. The function will be `undefined` if the current user does not have
 * permission to edit the featured brand pin status.
 * @param onError Callback function when an error occurs during the API call.
 * @param onSuccess Callback function when the API call succeeds. Receives the
 * resulting pinned state (true if pinned, false if unpinned).
 */
export function useToggleFeaturedBrandPin(
    onError?: CallbackFn,
    onSuccess?: SuccessCallbackFn,
) {
    const { isDesktop } = useAtomValue(uiContextAtomDisplayMode);
    const fetcher = useAppFetcher<{
        FeaturedBrandPinToggle?: { __typename: string; Message?: string };
    }>();
    const onErrorRef = useLatest<CallbackFn | undefined>(onError);
    const onSuccessRef = useLatest<SuccessCallbackFn | undefined>(onSuccess);
    const lastHandledDataRef = useRef<unknown>(null);
    const tracker = useTracker();
    const { user, isLoggedIn } = useUserLoggedIn();
    const { editProfile, editAdvertAdmin } = useRootPermissions();
    const eventData = useRef({
        ItemID: 0,
        ExtraData: {
            pinned: false,
            userId: 0,
            userName: '',
        },
    });

    const toggleFeaturedBrandPin = useCallback(
        (itemID: number, pin: boolean) => {
            if (itemID <= 0) {
                return;
            }
            eventData.current = {
                ItemID: itemID,
                ExtraData: {
                    pinned: pin,
                    userId: user.ID,
                    userName: isLoggedIn
                        ? `${user.FirstName} ${user.LastName}`
                        : '',
                },
            };
            fetcher.submit(
                {
                    itemID: itemID,
                    pin: pin,
                },
                {
                    method: 'post',
                    action: TOGGLE_FEATURED_BRAND_PIN,
                },
            );
        },
        [fetcher, user, isLoggedIn],
    );

    useEffect(() => {
        if (!fetcherIsDone(fetcher) || !fetcher.data) return;
        if (lastHandledDataRef.current === fetcher.data) return;
        lastHandledDataRef.current = fetcher.data;

        const result = fetcher.data.FeaturedBrandPinToggle;
        if (result?.__typename === 'UserError' && result.Message) {
            onErrorRef.current?.({
                __typename: result.__typename,
                Message: result.Message,
            });
        } else if (result) {
            onSuccessRef.current?.(eventData.current.ExtraData.pinned);
        }
    }, [fetcher, onErrorRef, onSuccessRef, tracker]);

    return {
        toggleFeaturedBrandPin:
            isDesktop && editProfile && editAdvertAdmin
                ? toggleFeaturedBrandPin
                : undefined,
    };
}
